// Ported from files/snapdraw-design-kit/code/src/routes/index.tsx (Lovable
// reference implementation), backlog LAUNCH-3. Public marketing page at
// `/` for signed-out visitors — App.tsx renders this instead of Dashboard
// whenever there's no session, so it never goes through requireAccess/
// AppShell. Router primitives swapped for this app's react-router-dom
// (the kit uses TanStack Router); the kit's own per-route <head> meta
// block was dropped — this is a client-rendered SPA with no per-route SSR
// meta pattern anywhere else in the app, and index.html's static tags
// already cover the same ground.
import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import { BarChart3, BookOpen, Camera, Check, FileSpreadsheet, FileText, Languages, Smartphone, Users } from "lucide-react";

import { BrandMark } from "@/components/snapdraw/brand";
import { LanguageToggle } from "@/components/snapdraw/language-toggle";
import { clearAuthLinkError, readAuthLinkError } from "@/lib/auth-link-error";
import { APP_STORE_URL, GOOGLE_PLAY_URL } from "@/lib/config";
import { useI18n, type Language } from "@/lib/i18n";
import { useDocumentTitle } from "@/lib/useDocumentTitle";
import "@/styles/tailwind-preview.css";

import appStoreBadgeEn from "@/assets/store-badges/download-on-app-store-en.svg";
import appStoreBadgeEs from "@/assets/store-badges/download-on-app-store-es.svg";
import googlePlayBadgeEn from "@/assets/store-badges/get-it-on-google-play-en.png";
import googlePlayBadgeEs from "@/assets/store-badges/disponible-en-google-play-es.png";

const STATS = [
  { valueKey: "landing.stat.quote.value", labelKey: "landing.stat.quote.label" },
  { valueKey: "landing.stat.g702.value", labelKey: "landing.stat.g702.label" },
  { valueKey: "landing.stat.lang.value", labelKey: "landing.stat.lang.label" },
  { valueKey: "landing.stat.seats.value", labelKey: "landing.stat.seats.label" },
] as const;

// Order matters (Lovable review, 2026-09-03): row 1 sells (the core job,
// then differentiators), row 2 supports (valuable, but not a first-visit
// reason to buy). Photos replaces the old theme card — a preference, not a
// reason to buy — with the newest, most visual, most demo-able capability.
const FEATURES = [
  { icon: FileText, titleKey: "landing.feature.quotes.title", bodyKey: "landing.feature.quotes.body" },
  { icon: FileSpreadsheet, titleKey: "landing.feature.aia.title", bodyKey: "landing.feature.aia.body" },
  { icon: Languages, titleKey: "landing.feature.bilingual.title", bodyKey: "landing.feature.bilingual.body" },
  { icon: Camera, titleKey: "landing.feature.photos.title", bodyKey: "landing.feature.photos.body" },
  { icon: Smartphone, titleKey: "landing.feature.mobile.title", bodyKey: "landing.feature.mobile.body" },
  { icon: BookOpen, titleKey: "landing.feature.catalog.title", bodyKey: "landing.feature.catalog.body" },
  { icon: BarChart3, titleKey: "landing.feature.reports.title", bodyKey: "landing.feature.reports.body" },
  { icon: Users, titleKey: "landing.feature.team.title", bodyKey: "landing.feature.team.body" },
] as const;

const INCLUDED = [
  "landing.pricing.included.1",
  "landing.pricing.included.2",
  "landing.pricing.included.3",
  "landing.pricing.included.4",
  "landing.pricing.included.5",
] as const;

// Per files/hero-watermark-CLAUDE-INSTRUCTIONS.md (2026-08-30): fills the
// hero's right column, which was reading as unfinished empty space on wide
// desktop screens (Lovable feedback, two rounds running). Decorative only
// (aria-hidden) — a blueprint-linework rendering of the Shaman Technologies
// mark, never the literal logo art (rejected in the spec: gradients go
// muddy on this dark field). Caption is a fixed proper noun, not run
// through t() — same precedent as the footer's own "© 2026 Shaman
// Technologies" line elsewhere on this page. Hidden below lg per spec —
// phone and tablet keep the plain stacked hero.
function HeroWatermark() {
  return (
    <div
      aria-hidden
      className="relative hidden w-[320px] translate-y-5 flex-col items-center justify-center lg:flex xl:w-[380px] xl:translate-y-7"
    >
      <div className="blueprint-grid absolute inset-0 opacity-40" />

      {/* Real-device review, light mode (2026-08-31): text-spark/20 was tuned
          against Blueprint Night only and read as nearly invisible on
          Blueprint Day's much lighter background — --watermark-color gives
          light mode its own, stronger value instead of a fixed alpha of a
          color that already changes register between themes. */}
      <svg
        viewBox="0 0 200 190"
        className="relative h-64 w-64 xl:h-72 xl:w-72"
        style={{ color: "var(--watermark-color)" }}
      >
        <path
          d="M100 14 L186 172 H14 Z"
          fill="currentColor"
          fillOpacity="0.06"
          stroke="currentColor"
          strokeWidth="1.5"
          vectorEffect="non-scaling-stroke"
        />
        <path
          d="M100 58 L152 150 H48 Z"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.25"
          vectorEffect="non-scaling-stroke"
        />
        <path
          d="M100 92 L124 112 L100 132 L76 112 Z"
          fill="none"
          stroke="currentColor"
          strokeWidth="1"
          vectorEffect="non-scaling-stroke"
        />
        <path
          d="M74 120 L100 146 L126 120"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          vectorEffect="non-scaling-stroke"
        />
        <path
          d="M100 4 V182 M4 172 H196"
          stroke="currentColor"
          strokeWidth="1"
          strokeDasharray="4 7"
          strokeOpacity="0.6"
          vectorEffect="non-scaling-stroke"
        />
      </svg>

      <p className="relative mt-6 font-mono text-[10px] uppercase tracking-[0.22em] text-foreground/35">
        A Shaman Technologies Product
      </p>

      <span className="pointer-events-none absolute left-0 top-8 h-14 w-14 border-l border-t border-spark/30" />
      <span className="pointer-events-none absolute bottom-8 right-0 h-14 w-14 border-b border-r border-spark/30" />

      <div className="pointer-events-none absolute left-1/2 top-1/2 h-64 w-64 -translate-x-1/2 -translate-y-1/2 rounded-full bg-spark/[2.5%] blur-3xl" />
    </div>
  );
}

// Store badge row (backlog LAUNCH-26). One URL per store, not one global
// "live" boolean — each badge renders only once its own store's URL is set,
// so staggered App Store/Play Store approval never claims an unapproved
// platform is downloadable. See src/lib/config.ts for the actual values;
// both are undefined (nothing renders) until a real review passes.
const MOBILE_STORE_URLS = { apple: APP_STORE_URL, google: GOOGLE_PLAY_URL };
const hasLiveMobileStore = Boolean(MOBILE_STORE_URLS.apple || MOBILE_STORE_URLS.google);

const STORE_BADGE_ASSETS = {
  en: { apple: appStoreBadgeEn, google: googlePlayBadgeEn },
  es: { apple: appStoreBadgeEs, google: googlePlayBadgeEs },
} as const;

// Official, unmodified Apple/Google badge artwork only — never recreated
// with JSX/text/Tailwind. Badges keep their own aspect ratio; only their
// height is fixed so the two sit at a consistent scale next to each other.
// Apple's SVG is tightly cropped (pill fills ~100% of the file height) in
// both locales, but Google's PNG bakes in clear-space that varies BY
// LOCALE (measured: pill fills ~67% of the English file's height, ~77% of
// the Spanish one's) — a single fixed height for Google looked visibly
// mismatched between languages, so its height is picked per-locale instead
// to keep both pills reading as the same visual size as Apple's.
const GOOGLE_BADGE_HEIGHT: Record<Language, string> = { en: "h-[60px]", es: "h-[52px]" };

// Never rendered inside the native apps themselves — App.tsx's "/" route
// shows this same LandingPage to any signed-out visitor regardless of
// platform, so a signed-out native user would otherwise see "Download on
// the App Store" from inside the app they already have installed. Same
// Capacitor.isNativePlatform() pattern Paywall.tsx already uses to hide
// its own web-only purchase UI on native.
function MobileStoreBadges() {
  const { t, lang } = useI18n();
  if (!hasLiveMobileStore || Capacitor.isNativePlatform()) return null;
  const badges = STORE_BADGE_ASSETS[lang];

  return (
    <div className="mt-6" aria-label={t("landing.mobileStores.label")}>
      <p className="sd-label text-[10px]">{t("landing.mobileStores.label")}</p>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        {MOBILE_STORE_URLS.apple ? (
          <a
            href={MOBILE_STORE_URLS.apple}
            target="_blank"
            rel="noreferrer"
            className="inline-flex rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-arc focus-visible:ring-offset-2 focus-visible:ring-offset-background"
          >
            <img src={badges.apple} alt={t("landing.mobileStores.appleAlt")} className="h-10 w-auto" />
          </a>
        ) : null}
        {MOBILE_STORE_URLS.google ? (
          <a
            href={MOBILE_STORE_URLS.google}
            target="_blank"
            rel="noreferrer"
            className="inline-flex rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-arc focus-visible:ring-offset-2 focus-visible:ring-offset-background"
          >
            <img
              src={badges.google}
              alt={t("landing.mobileStores.googleAlt")}
              className={`w-auto ${GOOGLE_BADGE_HEIGHT[lang]}`}
            />
          </a>
        ) : null}
      </div>
    </div>
  );
}

export function LandingPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  useDocumentTitle(t("landing.meta.title"), t("landing.meta.desc"));

  // Failed Supabase auth links (expired confirmation / recovery) redirect to
  // the site URL with an #error hash (backlog AUTH-13). Catch it and route
  // to a friendly "link expired" state on /login instead of silently
  // showing marketing with a scary URL. A post-redirect OAuth failure lands
  // on this exact same hash shape (backlog AUTH-16) — routed to its own
  // "sign-in didn't complete" state instead, since nothing about a link is
  // actually involved there.
  useEffect(() => {
    const err = readAuthLinkError();
    if (!err) return;
    clearAuthLinkError();
    navigate(err.fromOAuth ? "/login?link=oauth_error" : "/login?link=expired", { replace: true });
  }, [navigate]);

  return (
    <div className="blueprint-field relative min-h-screen overflow-hidden">
      {/* Corner registration marks */}
      <span className="pointer-events-none absolute left-6 top-6 h-10 w-10 border-l border-t border-border-strong/60" />
      <span className="pointer-events-none absolute bottom-6 right-6 h-10 w-10 border-b border-r border-border-strong/60" />

      {/* Nav */}
      {/* pt- uses env(safe-area-inset-top) additively (not max()) so the
          24px of breathing room below the status bar/notch stays constant
          across devices — resolves to plain 24px in any normal browser,
          since env() falls back to 0 there (found live in the Capacitor
          iOS Simulator, backlog MOBILE-1: "Log In" rendered clipped under
          the status bar with the old fixed pt-6). */}
      <header className="relative z-10 mx-auto flex w-full max-w-6xl items-center gap-4 px-6 pt-[calc(env(safe-area-inset-top)+1.5rem)] lg:px-10">
        <div className="flex items-center gap-3">
          <BrandMark size={40} />
          <span className="font-display text-2xl font-bold uppercase tracking-wide text-foreground">SnapDraw</span>
        </div>
        <div className="ml-auto flex items-center gap-3">
          <LanguageToggle />
          {/* Real bug found live on an actual phone (2026-08-29): this was
              `hidden sm:inline-flex` in the ported kit design, same as the
              CTA button below — but that left a returning customer with no
              way at all to log in from a phone (no other login link exists
              anywhere on this page). Now shows at every width; it's a
              small text link, not a padded button, so it fits fine next to
              the logo + language toggle even on a narrow phone. */}
          <Link
            to="/login"
            className="rounded-xl px-3 py-2 font-mono text-[11px] uppercase tracking-[0.16em] text-muted-foreground transition-colors hover:text-foreground sm:px-4"
          >
            {t("landing.nav.login")}
          </Link>
          {/* Real bug found live on an actual phone (2026-08-29): this
              button's fixed h-10 clipped the text once it wrapped to two
              lines at narrow widths, squeezed next to the logo + language
              toggle + Log in link. Hidden below sm: instead of shrinking
              the text — the hero's own equally prominent CTA is already in
              view in the same initial phone viewport, so nothing is lost. */}
          <Link
            to="/signup"
            className="hidden h-10 items-center rounded-xl bg-[image:var(--gradient-arc)] px-5 font-display text-lg uppercase tracking-wide text-primary-foreground transition-shadow hover:glow-arc sm:inline-flex"
          >
            {t("landing.nav.trial")}
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="relative z-10 mx-auto max-w-6xl px-6 pb-8 pt-16 lg:px-10 lg:pb-12 lg:pt-24">
        {/* Two-column at lg+ (files/hero-watermark-CLAUDE-INSTRUCTIONS.md,
            2026-08-30): the right column was reading as unfinished empty
            space on wide desktop screens. Below lg, HeroWatermark itself
            renders nothing (hidden lg:flex), so this stays the plain
            single-column stacked hero exactly as before. */}
        <div className="lg:grid lg:grid-cols-[1fr_auto] lg:items-center lg:gap-12">
          <div>
            <p className="sd-label text-xs">{t("landing.hero.eyebrow")}</p>
            <h1 className="mt-4 max-w-3xl text-[2.75rem] leading-[0.92] sm:text-6xl lg:text-[5.25rem]">
              {t("landing.hero.title.1")}
              <br />
              <span className="text-arc">{t("landing.hero.title.2")}</span>
            </h1>
            {/* max-w-xl (the kit's own value) orphaned "you." alone on a third
                line in English at typical desktop widths — same issue as the
                Dashboard welcome banner (LAUNCH-3); widened for the same reason. */}
            <p className="mt-6 max-w-2xl text-base text-foreground/80 lg:text-lg">{t("landing.hero.sub")}</p>

            <div className="mt-9 flex flex-col gap-4 sm:flex-row sm:items-center">
              <Link
                to="/signup"
                className="inline-flex h-14 items-center justify-center rounded-xl bg-[image:var(--gradient-arc)] px-9 font-display text-2xl uppercase tracking-wide text-primary-foreground transition-shadow hover:glow-arc"
              >
                {t("landing.hero.cta")}
              </Link>
              {/* Lovable feedback (2026-08-30): full arc-blue border + text gave
                  this near-equal visual weight to the gradient primary CTA right
                  next to it — two competing full-strength CTAs. Muted to a
                  quiet default state that only picks up arc on hover.
                  Revisited 2026-08-31 (Lovable review): the fully-neutral
                  border-strong/60 default read as lower-contrast than the
                  hero's own body copy. Landed on a middle ground rather than
                  reverting to full strength (explicit call, given the CTA-
                  competition reasoning above still applies) — a partial arc
                  tint as the default, hover unchanged. /35 was only checked
                  live in light; a same-day dark-mode screenshot showed it
                  still too faint against gunmetal, bumped to /55 — verified
                  in both themes this still reads as clearly secondary to the
                  solid gradient CTA (outline vs. filled, not just alpha, is
                  what keeps them from competing). */}
              <a
                href="#pricing"
                className="inline-flex h-14 items-center justify-center rounded-xl border border-arc/55 px-9 font-display text-2xl uppercase tracking-wide text-muted-foreground transition-colors hover:border-arc hover:text-arc"
              >
                {t("landing.hero.secondary")}
              </a>
            </div>
            <p className="sd-label mt-4 text-[10px]">{t("landing.hero.ctaNote")}</p>

            <MobileStoreBadges />

            {/* Lovable feedback (2026-08-29): a hairline rule above the stats
                gives them a "drawing title block" feel, consistent with the
                corner registration marks and mono eyebrow labels elsewhere on
                this page. **Strengthened 2026-08-30**: border-border/60 computed
                to ~10% effective opacity (--border is already a low-alpha
                token), confirmed live as too faint to actually read as a rule —
                bumped to border-border-strong/60, the same weight already used
                on this page's own corner registration marks. */}
            {/* Lovable review (2026-09-22): a 4-wide flex-wrap row let Spanish's
                longer labels ("COTIZACIÓN PROM.", "FACTURACIÓN AIA") push just
                the 4th stat onto its own orphaned line — this column's real
                width constraint comes from the hero's two-column grid (the
                watermark takes the right column at lg+), not the viewport, so
                a container query (same @[Npx] pattern as QuoteBuilderPage's
                line-item grid) reflows to a symmetric 2x2 before that happens,
                in either language, rather than a viewport breakpoint that
                can't tell English and Spanish content widths apart. */}
            <div className="@container mt-12 border-t border-border-strong/60 pt-8">
              <dl className="grid grid-cols-[repeat(2,max-content)] gap-x-8 gap-y-6 @[640px]:grid-cols-[repeat(4,max-content)] @[640px]:gap-x-12">
                {STATS.map((s) => (
                  <div key={s.labelKey}>
                    <dt className="sr-only">{t(s.labelKey)}</dt>
                    <dd className="sd-numeric text-2xl font-bold lg:text-3xl">{t(s.valueKey)}</dd>
                    <dd className="sd-label mt-1">{t(s.labelKey)}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>

          <HeroWatermark />
        </div>
      </section>

      {/* Features */}
      {/* Lovable feedback (2026-08-29): a large empty band was reading as
          dead space between the hero stats and this section's own eyebrow —
          the hero's bottom padding above and this section's top padding
          were both generous on their own but compounded into one big gap.
          Tightened both sides. Bottom padding (before Pricing) was
          originally left unchanged here, then halved 2026-08-31 (Lovable
          review, screenshot evidence of a ~200px dead band once combined
          with Pricing's own top padding below) — see that section's own
          comment for the other half. Halved again, same day: measured live,
          the gap above the grid (this section's own mt-12, a flat 48px at
          every breakpoint) vs. the gap below it (this pb + Pricing's own pt)
          was 48px vs ~101px — not just "a touch taller" as described, closer
          to double. Dropped the lg: escalation entirely so both sides land
          on the same flat 48px combined, matching the rhythm above the grid
          instead of scaling up at lg while the top of the grid doesn't. */}
      <section className="relative z-10 mx-auto max-w-6xl px-6 pb-6 pt-4 lg:px-10 lg:pt-8">
        <p className="sd-label text-xs">{t("landing.features.eyebrow")}</p>
        <h2 className="mt-3 text-4xl uppercase leading-[0.95] sm:text-5xl">{t("landing.features.title")}</h2>
        {/* Same orphan-word wrap as the hero sub and Dashboard banner
            (both LAUNCH-3) — max-w-xl (the kit's own value) stranded
            "paperwork." alone on a second line at typical desktop widths.
            Widened for the same reason. */}
        <p className="mt-4 max-w-2xl text-base text-foreground/75">{t("landing.features.sub")}</p>

        {/* lg:grid-cols-4 (Lovable review, 2026-09-03): the earlier
            xl:grid-cols-4 fix only closed the 3-3-2 ragged-row gap at xl+,
            leaving it open between lg and xl. A clean 4x2 at lg (2x4 at sm,
            single column on phones) removes the gap at every width instead
            of just the widest one. */}
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {FEATURES.map((f) => (
            <article key={f.titleKey} className="sd-card p-6">
              {/* Lovable feedback (2026-08-29): h-6 read slightly light
                  against the bold uppercase card titles; bumped size and
                  stroke weight to match. */}
              <f.icon className="h-7 w-7 text-arc" strokeWidth={1.75} />
              <h3 className="mt-4 font-display text-xl uppercase tracking-wide text-foreground">{t(f.titleKey)}</h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground/75">{t(f.bodyKey)}</p>
            </article>
          ))}
        </div>
      </section>

      {/* Pricing */}
      {/* pt halved 2026-08-31 (Lovable review), then dropped its lg:
          escalation too, same day — see the Features section's own comment
          for the measured before/after. pb is unchanged (bottom-of-page
          spacing wasn't the complaint). */}
      <section id="pricing" className="relative z-10 mx-auto max-w-6xl px-6 pb-16 pt-6 lg:px-10 lg:pb-24">
        {/* Lovable feedback (2026-08-29): items-start left the shorter
            pricing card visually stranded above awkward whitespace next to
            the taller included-features column. Center instead of matching
            heights — simpler, and the card reads fine at its natural size. */}
        <div className="grid items-center gap-10 lg:grid-cols-2">
          <div>
            <p className="sd-label text-xs">{t("landing.pricing.eyebrow")}</p>
            <h2 className="mt-3 text-4xl uppercase leading-[0.95] sm:text-5xl">{t("landing.pricing.title")}</h2>
            <p className="mt-4 max-w-md text-base text-foreground/75">{t("landing.pricing.sub")}</p>
            <ul className="mt-8 space-y-3">
              {INCLUDED.map((key) => (
                <li key={key} className="flex items-start gap-3 text-sm text-foreground/85">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-arc" />
                  {t(key)}
                </li>
              ))}
            </ul>
          </div>

          <div className="sd-card p-6 sm:p-8">
            {/* Real bug found live on an actual phone (2026-08-29): side by
                side, the plan name and badge competed for width narrowly
                enough that "SnapDraw — Full Access" broke as "SnapDraw —
                Full" / "Access" — an orphaned single word, not a clean
                wrap. Stacked below sm: so the name gets the card's full
                width to itself; unchanged side-by-side above sm:, where
                there was always room for both. */}
            <div className="flex flex-col items-start gap-3 border-b border-border pb-4 sm:flex-row sm:items-center sm:justify-between">
              <p className="font-display text-xl uppercase tracking-wide text-arc">{t("landing.pricing.plan")}</p>
              <span className="inline-flex shrink-0 items-center rounded-md border border-success px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.16em] text-success">
                {t("landing.pricing.introBadge")}
              </span>
            </div>

            <div className="mt-5 flex flex-wrap items-baseline gap-x-4 gap-y-1">
              <span className="sd-numeric text-5xl font-bold text-foreground">{t("landing.pricing.intro")}</span>
              <span className="text-sm text-muted-foreground">{t("landing.pricing.introNote")}</span>
            </div>
            <div className="mt-2 flex items-baseline gap-2">
              <span className="sd-numeric text-xl font-bold text-muted-foreground line-through">
                {t("landing.pricing.price")}
              </span>
              <span className="text-sm text-muted-foreground">{t("landing.pricing.perMonth")}</span>
            </div>

            <Link
              to="/signup"
              className="mt-7 flex h-14 w-full items-center justify-center rounded-xl bg-[image:var(--gradient-arc)] font-display text-2xl uppercase tracking-wide text-primary-foreground transition-shadow hover:glow-arc"
            >
              {t("landing.pricing.cta")}
            </Link>
            <p className="sd-label mt-4 text-center text-[10px]">{t("landing.pricing.trial")}</p>
            <p className="mt-1 text-center text-xs text-muted-foreground">{t("landing.pricing.cancel")}</p>
          </div>
        </div>
      </section>

      {/* Footer */}
      {/* border-border → border-border-strong (Lovable review, 2026-08-31):
          a rule already existed here — --border just wasn't visible enough
          to register as one at the bottom of the page's own empty field.
          Same token this page already uses for its corner marks and stats
          divider, not a new one. */}
      <footer className="relative z-10 border-t border-border-strong">
        <div className="mx-auto flex max-w-6xl flex-col items-center gap-4 px-6 py-10 text-center sm:flex-row sm:justify-between sm:text-left lg:px-10">
          {/* Tagline dropped (real phone feedback, 2026-08-29): it just
              repeated the hero headline directly above the fold, and on
              mobile added a cramped extra line right next to Terms/Privacy.
              landing.footer.tagline stays in the dictionary unused rather
              than removed — cheap to bring back if this reads differently
              on a real desktop screen. */}
          <div className="flex items-center gap-3">
            <BrandMark size={32} />
            <div className="font-display text-lg font-bold uppercase tracking-wide text-foreground">SnapDraw</div>
          </div>
          {/* Lovable feedback (2026-08-29): sd-label's default muted-
              foreground color read too close to the floor down here —
              bumped one step brighter (text-foreground/60 overrides it)
              without going all the way to full foreground. Bumped again to
              /75 (2026-08-31) — a fresh review called this the weakest-
              contrast element on the whole light-mode page; confirmed live
              against the actual computed color before landing here. */}
          <p className="sd-label flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-[10px] text-foreground/75">
            <span>© 2026 Shaman Technologies</span>
            <span aria-hidden>·</span>
            <span>{t("landing.footer.domain")}</span>
            <span aria-hidden>·</span>
            <Link to="/terms" className="hover:text-arc">
              {t("legal.terms")}
            </Link>
            <span aria-hidden>·</span>
            <Link to="/privacy" className="hover:text-arc">
              {t("legal.privacy")}
            </Link>
            <span aria-hidden>·</span>
            <Link to="/support" className="hover:text-arc">
              {t("legal.support")}
            </Link>
          </p>
        </div>
      </footer>
    </div>
  );
}
